import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AppService } from '../app.service';
import { UtilsService } from '../shared/utils.service';

/**
 * Subscription plan route guard.
 *
 * Blocks access to a route whose feature area is not included in the active account's subscription
 * tier, and redirects to the dashboard with an upgrade message. This mirrors how RBAC page-level
 * access is handled, but for the coarse subscription gate (see AppService.planCan).
 *
 * Usage: add to a route's canActivate with the feature key in route data, e.g.
 *   {
 *     path: 'incidents',
 *     component: IncidentsComponent,
 *     canActivate: [AuthGuard, PlanGuard],
 *     data: { planFeature: AppService.FEATURES.INCIDENTS }
 *   }
 *
 * Super admins always pass (planCan bypasses for is_sitesoft_admin). The API remains the
 * authoritative boundary via the EnsurePlanFeature middleware.
 */
@Injectable({
  providedIn: 'root'
})
export class PlanGuard {

  constructor(
    private app: AppService,
    private utils: UtilsService
  ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const feature: string = next.data ? next.data['planFeature'] : null;

    // No feature configured on the route: nothing to gate.
    if ( !feature ) {
      return true;
    }

    if ( !this.app.planCan(feature) ) {
      // Matches the API wording. It names no tier, because which tier unlocks a feature varies now
      // that Contractor sits between Free and Premium.
      this.utils.showToast('This feature is not included in your current subscription. Please contact your account manager to upgrade.');
      this.utils.redirectPage(['/dashboard']);
      return false;
    }

    return true;
  }
}
